import React, { useCallback, useMemo, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { Translate, withLocalize } from 'react-localize-redux';
import { PageWrapper, PageHeader } from '../components/partials';
import Spinner from '../components/widgets/Spinner';
import Button from '../components/atoms/Button';
import { IconSizes } from '../components/foundations/Icon';
import GeneralPopup, { PopupTypes } from '../components/organisms/GeneralPopup';
import toaster from '../utils/toaster';
import useAuthedChat from '../chatView/hooks/useAuthedChat';
import StreamMonitor from '../chatView/components/StreamMonitor';
import SessionNoticeToasts from '../chatView/components/SessionNoticeToasts';
import { EVENT_TYPES, SESSION_TYPES } from '../chatView/constants';
import useMonitors from './hooks/useMonitors';
import usePromptSuggestion from './hooks/usePromptSuggestion';
import { MonitorApiError } from './service';
import AddMonitorBar from './components/AddMonitorBar';
import WatchlistEmptyState from './components/WatchlistEmptyState';
import WatchlistExamplesStrip from './components/WatchlistExamplesStrip';
import MonitorCard from './components/MonitorCard';
import SummaryChips from './components/SummaryChips';
import ScheduleModal from './components/ScheduleModal';
import ThreadShelf from './components/ThreadShelf';
import {
    CARD_STATES,
    DEFAULT_SCHEDULE,
    MONITOR_STATUS,
    MONITOR_VERDICT_EVENT,
    RUN_ERROR_TOAST_KEYS,
    TOAST_AUTO_CLOSE_MS,
    TRANSLATION_KEY,
    WATCHLIST_EVENTS,
    trackWatchlistEvent,
} from './constants';
import { getCardState, getDisplayVerdict, presetToCron } from './utils';
import css from './page.css';

const Watchlist = ({ translate }) => {
    const [openMonitorId, setOpenMonitorId] = useState(null);
    const { chat, authLoading, authError } = useAuthedChat({
        sessionType: SESSION_TYPES.MONITOR,
        isViewingConversation: openMonitorId != null,
    });
    const monitorsApi = useMonitors({ attachRun: chat.attachRun });
    const { monitors, loadError, liveState } = monitorsApi;

    const [draftCron, setDraftCron] = useState(presetToCron(DEFAULT_SCHEDULE));
    const [creating, setCreating] = useState(false);
    const [createError, setCreateError] = useState(null);
    const [scheduleModal, setScheduleModal] = useState(null);
    const [scheduleError, setScheduleError] = useState(null);
    const [scheduleSaving, setScheduleSaving] = useState(false);
    const [monitorToDelete, setDeleteTarget] = useState(null);
    const [deleting, setDeleting] = useState(false);
    const [runningAll, setRunningAll] = useState(false);
    const { suggestion, pickSuggestion, clearSuggestion } = usePromptSuggestion();

    const monitorCountRef = useRef(0);
    monitorCountRef.current = monitors?.length ?? 0;

    // Losing the last monitor swaps in the empty-state bar, which would otherwise remount onto a stale pick.
    const forgetSuggestionOnLastRemoval = useCallback(() => {
        if (monitorCountRef.current === 1) clearSuggestion();
    }, [clearSuggestion]);

    const live = useMemo(
        () => ({ ...liveState, streamingSessionIds: chat.streamingSessionIds }),
        [liveState, chat.streamingSessionIds]
    );

    const cardStates = useMemo(
        () => new Map((monitors ?? []).map(m => [m.monitor_id, getCardState(m, live)])),
        [monitors, live]
    );

    const showErrorToast = key =>
        toaster.error(translate(`${TRANSLATION_KEY}.TOASTS.${key}`), { autoClose: TOAST_AUTO_CLOSE_MS });

    const closeShelf = useCallback(() => setOpenMonitorId(null), []);

    const handleMonitorGone = useCallback(monitor => {
        forgetSuggestionOnLastRemoval();
        monitorsApi.removeMonitorLocally(monitor.monitor_id);
        chat.purgeSessionState(monitor.session_id);
        setOpenMonitorId(current => (current === monitor.monitor_id ? null : current));
        showErrorToast('MONITOR_DELETED');
    }, [forgetSuggestionOnLastRemoval, monitorsApi.removeMonitorLocally, chat.purgeSessionState]);

    const handleStreamEvent = useCallback((sessionId, runId, message) => {
        if (message.type === MONITOR_VERDICT_EVENT) monitorsApi.onCheckVerdict(sessionId, runId, message.data);
        else if (message.type === EVENT_TYPES.FINISH) monitorsApi.onCheckFinish(sessionId, runId, message.finishReason);
        chat.handleStreamMessage(sessionId, message);
    }, [monitorsApi.onCheckVerdict, monitorsApi.onCheckFinish, chat.handleStreamMessage]);

    const openThread = useCallback(monitor => {
        trackWatchlistEvent(WATCHLIST_EVENTS.OPEN_THREAD, { monitor_id: monitor.monitor_id });
        setOpenMonitorId(monitor.monitor_id);
        chat.loadSession(monitor.session_id);
    }, [chat.loadSession]);

    const handleCreate = async userQuery => {
        setCreating(true);
        setCreateError(null);
        try {
            await monitorsApi.addMonitor(userQuery, draftCron);
            clearSuggestion();
            return true;
        } catch (error) {
            if (error instanceof MonitorApiError && error.status === 429) {
                setCreateError(translate(`${TRANSLATION_KEY}.ADD_BAR.QUOTA_ERROR`));
            } else if (error instanceof MonitorApiError && error.status === 422 && error.detail) {
                setCreateError(error.detail);
            } else {
                showErrorToast('GENERIC_ERROR');
            }
            return false;
        } finally {
            setCreating(false);
        }
    };

    const handleRunNow = async monitor => {
        try {
            await monitorsApi.recheck(monitor);
        } catch (error) {
            if (!(error instanceof MonitorApiError)) return showErrorToast('GENERIC_ERROR');
            if (error.status === 404) return handleMonitorGone(monitor);
            if (error.status === 409 && RUN_ERROR_TOAST_KEYS[error.detail]) {
                return toaster.error(translate(RUN_ERROR_TOAST_KEYS[error.detail]), { autoClose: TOAST_AUTO_CLOSE_MS });
            }
            if (error.status === 502) return showErrorToast('RUN_FAILED');
            return showErrorToast('GENERIC_ERROR');
        }
    };

    const handleRunAll = async () => {
        if (runningAll) return;
        setRunningAll(true);
        try {
            await monitorsApi.recheckAllMonitors();
        } catch {
            showErrorToast('GENERIC_ERROR');
        } finally {
            setRunningAll(false);
        }
    };

    const handlePauseToggle = async monitor => {
        try {
            await monitorsApi.setPaused(monitor.monitor_id, monitor.status !== MONITOR_STATUS.PAUSED);
        } catch (error) {
            if (error instanceof MonitorApiError && error.status === 404) return handleMonitorGone(monitor);
            if (error instanceof MonitorApiError && error.status === 409) return showErrorToast('UNPAUSE_IN_PROGRESS');
            showErrorToast('GENERIC_ERROR');
        }
    };

    const handleSavePrompt = async (monitor, userQuery) => {
        try {
            await monitorsApi.savePrompt(monitor.monitor_id, userQuery);
        } catch (error) {
            if (error instanceof MonitorApiError && error.status === 404) return handleMonitorGone(monitor);
            showErrorToast('GENERIC_ERROR');
            throw error;
        }
    };

    const handleScheduleSave = async cron => {
        const monitor = scheduleModal?.monitor;
        if (!monitor) {
            setDraftCron(cron);
            setScheduleModal(null);
            return;
        }
        if (cron === monitor.schedule) {
            setScheduleModal(null);
            return;
        }
        setScheduleSaving(true);
        setScheduleError(null);
        try {
            await monitorsApi.saveSchedule(monitor.monitor_id, cron);
            setScheduleModal(null);
        } catch (error) {
            if (error instanceof MonitorApiError && error.status === 422 && error.detail) setScheduleError(error.detail);
            else if (error instanceof MonitorApiError && error.status === 404) {
                handleMonitorGone(monitor);
                setScheduleModal(null);
            } else {
                showErrorToast('GENERIC_ERROR');
            }
        } finally {
            setScheduleSaving(false);
        }
    };

    const handleDeleteConfirmed = async () => {
        setDeleting(true);
        try {
            forgetSuggestionOnLastRemoval();
            await monitorsApi.removeMonitor(monitorToDelete);
            chat.purgeSessionState(monitorToDelete.session_id);
            setOpenMonitorId(current => (current === monitorToDelete.monitor_id ? null : current));
        } catch {
            showErrorToast('GENERIC_ERROR');
        } finally {
            setDeleting(false);
            setDeleteTarget(null);
        }
    };

    if (authLoading) return <Spinner show expanded />;

    const openMonitor = monitors?.find(m => m.monitor_id === openMonitorId) ?? null;
    const hasIdleMonitor = (monitors ?? []).some(m => m.status === MONITOR_STATUS.IDLE);

    const addBarProps = {
        onSubmit: handleCreate,
        scheduleCron: draftCron,
        onScheduleClick: () => setScheduleModal({ monitor: null }),
        submitting: creating,
        error: createError,
        suggestion,
    };

    const content = () => {
        if (authError || (loadError && monitors == null)) {
            return (
                <div className={css.errorState}>
                    <Translate id={`${TRANSLATION_KEY}.LOAD_ERROR`} />
                    {!authError && (
                        <Button type="secondary" onClick={monitorsApi.fetchNow}>
                            <Translate id={`${TRANSLATION_KEY}.RETRY`} />
                        </Button>
                    )}
                </div>
            );
        }
        if (monitors == null) {
            return (
                <div className={css.list}>
                    {[1, 2, 3].map(i => (
                        <div key={i} data-testid="monitor-skeleton" className={css.skeleton} />
                    ))}
                </div>
            );
        }
        if (!monitors.length) {
            return <WatchlistEmptyState {...addBarProps} onPickSuggestion={pickSuggestion} />;
        }
        return (
            <>
                <AddMonitorBar {...addBarProps} />
                <WatchlistExamplesStrip onPick={pickSuggestion} />
                <div className={css.toolbar}>
                    <SummaryChips monitors={monitors} cardStates={cardStates} liveRuns={liveState.liveRuns} />
                    <Button
                        type="secondary"
                        className={css.runAll}
                        Icon="refresh"
                        iconSize={IconSizes.X_SMALL}
                        iconClass={classNames({ [css.spinning]: runningAll })}
                        disabled={runningAll || !hasIdleMonitor}
                        onClick={handleRunAll}
                    >
                        <Translate id={`${TRANSLATION_KEY}.RUN_ALL`} />
                    </Button>
                </div>
                <div className={css.list}>
                    {monitors.map(monitor => (
                        <MonitorCard
                            key={monitor.monitor_id}
                            monitor={monitor}
                            cardState={cardStates.get(monitor.monitor_id)}
                            verdict={getDisplayVerdict(monitor, liveState.liveRuns[monitor.session_id])}
                            hasUpdate={chat.updatedSessionIds.includes(monitor.session_id)}
                            onOpenThread={() => openThread(monitor)}
                            onRunNow={() => handleRunNow(monitor)}
                            onOpenSchedule={() => {
                                setScheduleError(null);
                                setScheduleModal({ monitor });
                            }}
                            onSavePrompt={userQuery => handleSavePrompt(monitor, userQuery)}
                            onPauseToggle={() => handlePauseToggle(monitor)}
                            onRemove={() => setDeleteTarget(monitor)}
                        />
                    ))}
                </div>
            </>
        );
    };

    return (
        <PageWrapper className={css.page}>
            <PageHeader text={`${TRANSLATION_KEY}.TITLE`} />
            {content()}
            {chat.streamDescriptors.map(({ key, runId }) => (
                <StreamMonitor
                    key={runId}
                    sessionKey={key}
                    runId={runId}
                    onMessage={(sessionId, message) => handleStreamEvent(sessionId, runId, message)}
                    onError={(sessionId, errorMessage) => chat.handleStreamError(sessionId, errorMessage)}
                />
            ))}
            <SessionNoticeToasts chat={chat} onSessionGone={closeShelf} />
            <ScheduleModal
                open={scheduleModal != null}
                cron={scheduleModal?.monitor ? scheduleModal.monitor.schedule : draftCron}
                isNew={scheduleModal?.monitor == null}
                onSave={handleScheduleSave}
                onClose={() => setScheduleModal(null)}
                saving={scheduleSaving}
                error={scheduleError}
            />
            <ThreadShelf
                open={openMonitor != null}
                monitor={openMonitor}
                verdict={openMonitor ? getDisplayVerdict(openMonitor, liveState.liveRuns[openMonitor.session_id]) : null}
                chat={chat}
                onClose={closeShelf}
                onSavePrompt={userQuery => handleSavePrompt(openMonitor, userQuery)}
            />
            <GeneralPopup
                open={monitorToDelete != null}
                type={PopupTypes.DELETE}
                title={`${TRANSLATION_KEY}.DELETE_MODAL.TITLE`}
                text={`${TRANSLATION_KEY}.DELETE_MODAL.MESSAGE`}
                acceptText={`${TRANSLATION_KEY}.DELETE_MODAL.ACCEPT`}
                rejectText={`${TRANSLATION_KEY}.DELETE_MODAL.REJECT`}
                onAccept={handleDeleteConfirmed}
                onReject={() => setDeleteTarget(null)}
                showSpinner={deleting}
            />
        </PageWrapper>
    );
};

Watchlist.propTypes = {
    translate: PropTypes.func.isRequired,
};

export default withLocalize(Watchlist);
